import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { IconType } from 'react-icons';
import { FiUsers, FiCode, FiLayers, FiCpu } from 'react-icons/fi';

// Animation variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12
    }
  }
};

interface ActionCard {
  title: string;
  description: string;
  icon: IconType;
  color: string;
  link: string;
}

interface ActionCardsProps {
  title?: string;
  cards?: ActionCard[];
  darkMode: boolean;
  staticMode?: boolean;
}

export const ActionCards: React.FC<ActionCardsProps> = ({ 
  title = "Unlock the Power of AI Collaboration",
  darkMode,
  staticMode = false,
  cards = [
    {
      title: "Persona Forge",
      description: "Create and customize AI personas with specialized capabilities",
      icon: FiUsers,
      color: "indigo",
      link: "/personas/forge"
    },
    {
      title: "Chat with Personas",
      description: "Engage in conversations with specialized AI personas",
      icon: FiCode,
      color: "emerald",
      link: "/chat/sessions"
    },
    {
      title: "View & Export Artifacts",
      description: "Access, manage and export generated content and solutions",
      icon: FiLayers,
      color: "amber",
      link: "/artifacts"
    },
    {
      title: "Workflows",
      description: "Design and execute multi-stage AI collaboration processes",
      icon: FiCpu,
      color: "purple",
      link: "/workflows"
    }
  ]
}) => {
  return (
    <div className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.h2 
          className={`text-3xl font-bold mb-12 text-center ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {title}
        </motion.h2>
        
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {cards.map((card, index) => {
            const Icon = card.icon;
            return (
              <motion.div 
                key={index} 
                variants={itemVariants} 
                whileHover={{ 
                  scale: 1.05, 
                  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" 
                }} 
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                {staticMode ? (
                  <div className={`${darkMode ? `bg-${card.color}-900 hover:bg-${card.color}-800` : `bg-${card.color}-600 hover:bg-${card.color}-700`} text-white p-6 rounded-lg flex flex-col items-center shadow-lg h-full`}>
                    <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center mb-4">
                      <Icon className="text-3xl" />
                    </div>
                    <h2 className="text-xl font-semibold mb-2">{card.title}</h2>
                    <p className="text-sm opacity-90 text-center">{card.description}</p>
                  </div>
                ) : (
                  <Link to={card.link} className={`${darkMode ? `bg-${card.color}-900 hover:bg-${card.color}-800` : `bg-${card.color}-600 hover:bg-${card.color}-700`} text-white p-6 rounded-lg flex flex-col items-center shadow-lg h-full`}>
                    <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center mb-4">
                      <Icon className="text-3xl" />
                    </div>
                    <h2 className="text-xl font-semibold mb-2">{card.title}</h2>
                    <p className="text-sm opacity-90 text-center">{card.description}</p>
                  </Link>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
};
